import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Typography, 
  Space, 
  Button,
  Table,
  Tag,
  Progress,
  List,
  Avatar,
  Tabs,
  Alert,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Badge,
  Timeline,
  Divider
} from 'antd';
import {
  TeamOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  DollarOutlined,
  CalendarOutlined,
  FileTextOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  FallOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  AlertOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Employee, HRMetrics, OnboardingTask } from '../types';
import type { ColumnsType } from 'antd/es/table';
import EmployeeManagement from '../components/EmployeeManagement';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface AttendanceRecord {
  id: string;
  employee: { firstName: string; lastName: string; employeeId: string };
  date: string;
  checkIn: string;
  checkOut: string;
  hoursWorked: number;
  status: string;
}

interface LeaveRequest {
  id: string;
  employee: { firstName: string; lastName: string };
  leaveType: string;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
  approvalStatus: string;
}

const HROverview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Function to get active tab from route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/hr/employees')) return 'employees';
    if (path.includes('/hr/payroll')) return 'payroll';
    if (path.includes('/hr/attendance')) return 'attendance';
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [isEmployeeModalVisible, setIsEmployeeModalVisible] = useState(false);
  const [isAttendanceModalVisible, setIsAttendanceModalVisible] = useState(false);
  const [isLeaveModalVisible, setIsLeaveModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  const [metrics, setMetrics] = useState<HRMetrics>({
    totalEmployees: 156,
    activeEmployees: 148,
    newHires: 12,
    terminations: 3,
    turnoverRate: 8.5,
    averageSalary: 75000,
    openPositions: 8,
    pendingOnboarding: 5,
    upcomingReviews: 23,
    employeesByDepartment: {
      'Engineering': 45,
      'Sales': 32,
      'Marketing': 18,
      'HR': 8,
      'Finance': 12,
      'Operations': 33
    },
    employeesByPosition: {
      'Software Engineer': 35,
      'Sales Representative': 25,
      'Marketing Specialist': 15,
      'Manager': 18,
      'Director': 8
    },
    payrollCosts: {
      current: 1250000,
      previous: 1180000,
      variance: 5.9
    }
  });

  const [recentHires, setRecentHires] = useState<Employee[]>([
    {
      id: '1',
      employeeId: 'EMP001',
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@company.com',
      phone: '+1234567890',
      department: 'Engineering',
      position: 'Software Engineer',
      hireDate: '2024-06-15',
      status: 'active',
      salary: 85000,
      employmentType: 'full-time',
      workLocation: 'hybrid',
      createdAt: '2024-06-15T10:00:00Z',
      updatedAt: '2024-06-15T10:00:00Z'
    },
    {
      id: '2',
      employeeId: 'EMP002',
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.johnson@company.com',
      phone: '+1234567891',
      department: 'Marketing',
      position: 'Marketing Manager',
      hireDate: '2024-06-10',
      status: 'active',
      salary: 75000,
      employmentType: 'full-time',
      workLocation: 'office',
      createdAt: '2024-06-10T09:00:00Z',
      updatedAt: '2024-06-10T09:00:00Z'
    }
  ]);

  const [pendingTasks, setPendingTasks] = useState<OnboardingTask[]>([
    {
      id: '1',
      title: 'Complete I-9 Form',
      description: 'Employee must complete employment eligibility verification',
      category: 'documentation',
      priority: 'high',
      dueDate: '2024-06-25',
      status: 'pending'
    },
    {
      id: '2',
      title: 'Setup Computer Equipment',
      description: 'IT department to provide laptop and access credentials',
      category: 'equipment',
      priority: 'high',
      dueDate: '2024-06-24',
      status: 'in-progress'
    },
    {
      id: '3',
      title: 'Safety Training',
      description: 'Complete mandatory workplace safety training module',
      category: 'training',
      priority: 'medium',
      dueDate: '2024-06-28',
      status: 'pending'
    }
  ]);

  const quickActions = [
    {
      title: 'Add Employee',
      icon: <UserAddOutlined />,
      color: '#52c41a',
      path: '/hr/employees',
      description: 'Add new employee to system'
    },
    {
      title: 'Process Payroll',
      icon: <DollarOutlined />,
      color: '#1890ff',
      path: '/hr/payroll',
      description: 'Run payroll for current period'
    },
    {
      title: 'Review Timesheets',
      icon: <ClockCircleOutlined />,
      color: '#fa8c16',
      path: '/hr/attendance',
      description: 'Approve pending timesheets'
    },
    {
      title: 'Post Job',
      icon: <FileTextOutlined />,
      color: '#722ed1',
      path: '/hr/recruitment',
      description: 'Create new job posting'
    }
  ];

  const departmentData = Object.entries(metrics.employeesByDepartment).map(([dept, count]) => ({
    department: dept,
    employees: count,
    percentage: ((count / metrics.totalEmployees) * 100).toFixed(1)
  }));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'in-progress': return 'blue';
      case 'completed': return 'green';
      case 'overdue': return 'red';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const employeeColumns: ColumnsType<Employee> = [
    {
      title: 'Employee ID',
      dataIndex: 'employeeId',
      key: 'employeeId',
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Department',
      dataIndex: ['department', 'name'],
      key: 'department',
    },
    {
      title: 'Position',
      dataIndex: ['position', 'name'],
      key: 'position',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge 
          status={status === 'ACTIVE' ? 'success' : 'default'} 
          text={status} 
        />
      ),
    },
    {
      title: 'Hire Date',
      dataIndex: 'hireDate',
      key: 'hireDate',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button size="small">Edit</Button>
          <Button size="small">View</Button>
        </Space>
      ),
    },
  ];

  const attendanceColumns: ColumnsType<AttendanceRecord> = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) => `${record.employee.firstName} ${record.employee.lastName} (${record.employee.employeeId})`,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Check In',
      dataIndex: 'checkIn',
      key: 'checkIn',
    },
    {
      title: 'Check Out',
      dataIndex: 'checkOut',
      key: 'checkOut',
    },
    {
      title: 'Hours Worked',
      dataIndex: 'hoursWorked',
      key: 'hoursWorked',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'PRESENT' ? 'green' : 'red'}>{status}</Tag>
      ),
    },
  ];

  const leaveColumns: ColumnsType<LeaveRequest> = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) => `${record.employee.firstName} ${record.employee.lastName}`,
    },
    {
      title: 'Leave Type',
      dataIndex: 'leaveType',
      key: 'leaveType',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days',
    },
    {
      title: 'Status',
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      render: (status) => (
        <Tag color={
          status === 'APPROVED' ? 'green' : 
          status === 'REJECTED' ? 'red' : 'orange'
        }>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.approvalStatus === 'PENDING' && (
            <>
              <Button size="small" type="primary">Approve</Button>
              <Button size="small" danger>Reject</Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const handleAddEmployee = async (values: any) => {
    setLoading(true);
    try {
      // API call would go here
      console.log('Adding employee:', values);
      setIsEmployeeModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error adding employee:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Employees"
              value={employees.length}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Present Today"
              value={attendance.filter(a => a.status === 'PRESENT').length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending Leaves"
              value={leaveRequests.filter(l => l.approvalStatus === 'PENDING').length}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg. Attendance"
              value={96.5}
              suffix="%"
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Department Distribution" style={{ height: 400 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Engineering</span>
                  <span>45%</span>
                </div>
                <Progress percent={45} strokeColor="#1890ff" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Marketing</span>
                  <span>25%</span>
                </div>
                <Progress percent={25} strokeColor="#52c41a" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Sales</span>
                  <span>20%</span>
                </div>
                <Progress percent={20} strokeColor="#faad14" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>HR</span>
                  <span>10%</span>
                </div>
                <Progress percent={10} strokeColor="#f5222d" />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Recent Activities" style={{ height: 400 }}>
            <Timeline>
              <Timeline.Item color="green">
                <p>John Doe checked in at 9:00 AM</p>
                <p style={{ color: '#999', fontSize: '12px' }}>Today, 9:00 AM</p>
              </Timeline.Item>
              <Timeline.Item color="blue">
                <p>Jane Smith submitted leave request</p>
                <p style={{ color: '#999', fontSize: '12px' }}>Yesterday, 3:30 PM</p>
              </Timeline.Item>
              <Timeline.Item color="orange">
                <p>Payroll processed for June 2024</p>
                <p style={{ color: '#999', fontSize: '12px' }}>2 days ago</p>
              </Timeline.Item>
              <Timeline.Item>
                <p>New employee onboarding completed</p>
                <p style={{ color: '#999', fontSize: '12px' }}>3 days ago</p>
              </Timeline.Item>
            </Timeline>
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1>Human Resources</h1>
        <Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setIsEmployeeModalVisible(true)}
          >
            Add Employee
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Overview" key="overview">
          {renderOverview()}
        </TabPane>
        
        <TabPane tab="Employees" key="employees">
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input.Search placeholder="Search employees..." style={{ width: 300 }} />
                <Select placeholder="Department" style={{ width: 150 }}>
                  <Option value="engineering">Engineering</Option>
                  <Option value="marketing">Marketing</Option>
                  <Option value="sales">Sales</Option>
                </Select>
                <Select placeholder="Status" style={{ width: 120 }}>
                  <Option value="ACTIVE">Active</Option>
                  <Option value="INACTIVE">Inactive</Option>
                </Select>
              </Space>
            </div>
            <Table 
              columns={employeeColumns} 
              dataSource={employees}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Attendance" key="attendance">
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <RangePicker />
                <Select placeholder="Employee" style={{ width: 200 }}>
                  {employees.map(emp => (
                    <Option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </Option>
                  ))}
                </Select>
                <Button 
                  type="primary" 
                  icon={<ClockCircleOutlined />}
                  onClick={() => setIsAttendanceModalVisible(true)}
                >
                  Record Attendance
                </Button>
              </Space>
            </div>
            <Table 
              columns={attendanceColumns} 
              dataSource={attendance}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Leave Management" key="leaves">
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Select placeholder="Leave Type" style={{ width: 150 }}>
                  <Option value="VACATION">Vacation</Option>
                  <Option value="SICK">Sick</Option>
                  <Option value="PERSONAL">Personal</Option>
                </Select>
                <Select placeholder="Status" style={{ width: 120 }}>
                  <Option value="PENDING">Pending</Option>
                  <Option value="APPROVED">Approved</Option>
                  <Option value="REJECTED">Rejected</Option>
                </Select>
                <Button 
                  type="primary" 
                  icon={<CalendarOutlined />}
                  onClick={() => setIsLeaveModalVisible(true)}
                >
                  New Leave Request
                </Button>
              </Space>
            </div>
            <Table 
              columns={leaveColumns} 
              dataSource={leaveRequests}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Payroll" key="payroll">
          <Card title="Payroll Management">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Card>
                  <Statistic
                    title="Total Payroll (This Month)"
                    value={145000}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card>
                  <Statistic
                    title="Pending Approvals"
                    value={3}
                    prefix={<AlertOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card>
                  <Statistic
                    title="Processed This Month"
                    value={45}
                    prefix={<TeamOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>
            <Divider />
            <Space>
              <Button type="primary">Generate Payroll</Button>
              <Button>Download Reports</Button>
              <Button>Tax Settings</Button>
            </Space>
          </Card>
        </TabPane>

        <TabPane tab="Employee Management" key="employee-management">
          <EmployeeManagement />
        </TabPane>
      </Tabs>

      {/* Add Employee Modal */}
      <Modal
        title="Add New Employee"
        open={isEmployeeModalVisible}
        onCancel={() => {
          setIsEmployeeModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleAddEmployee}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="department" label="Department" rules={[{ required: true }]}>
                <Select>
                  <Option value="engineering">Engineering</Option>
                  <Option value="marketing">Marketing</Option>
                  <Option value="sales">Sales</Option>
                  <Option value="hr">HR</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="position" label="Position" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="salary" label="Salary" rules={[{ required: true }]}>
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="hireDate" label="Hire Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Add Employee
              </Button>
              <Button onClick={() => {
                setIsEmployeeModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HROverview; 